'use client';

import { Archive, ArrowCounterclockwise, Bell, Check2Square } from 'react-bootstrap-icons';

import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSText } from '@repo/sds/SDSText';

import './HeaderNotificationContent.scss';

/**
 * Square with a dot in the top-right corner — mirrors Check2Square's box
 * but replaces the checkmark with a dot to signal "mark as unread".
 */
function DotSquare({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill="currentColor"
      viewBox="0 0 16 16"
      role="img"
      aria-label="Mark as unread"
    >
      <path d="M3 14.5A1.5 1.5 0 0 1 1.5 13V3A1.5 1.5 0 0 1 3 1.5h6a.5.5 0 0 1 0 1H3a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V8a.5.5 0 0 1 1 0v5a1.5 1.5 0 0 1-1.5 1.5z" />
      <circle cx="13" cy="3" r="1.75" />
    </svg>
  );
}

interface HeaderNotificationContentProps {
  title: string;
  dateLabel: string;
  message: string;
  isUnread?: boolean;
  isArchivedView?: boolean;
  onMarkAsRead?: () => void;
  onMarkAsUnread?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  dataTestId?: string;
  unreadDotDataTestId?: string;
  markReadDataTestId?: string;
  markUnreadDataTestId?: string;
  archiveDataTestId?: string;
  unarchiveDataTestId?: string;
}

export function HeaderNotificationContent({
  title,
  dateLabel,
  message,
  isUnread = false,
  isArchivedView = false,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onUnarchive,
  dataTestId,
  unreadDotDataTestId,
  markReadDataTestId,
  markUnreadDataTestId,
  archiveDataTestId,
  unarchiveDataTestId,
}: HeaderNotificationContentProps) {
  return (
    <div className="HeaderNotificationContent" data-testid={dataTestId}>
      <SDSFlexbox gap="text-large" alignItems="flex-start">
        <SDSFlexbox
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="HeaderNotificationContent__status-icon"
        >
          <SDSIcon className="icon-status-alert" icon={<Bell />} color="muted" ariaLabel="Notification" />
        </SDSFlexbox>

        <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start" fullWidth>
          <SDSFlexbox alignItems="center" gap="text-small" fullWidth className="HeaderNotificationContent__heading">
            <SDSText type="body-medium-bold" text={title} className="HeaderNotificationContent__title" />

            <SDSFlexbox className="HeaderNotificationContent__meta" alignItems="center" gap="text-small">
              <SDSText type="body-small" color="muted" text={dateLabel} className="HeaderNotificationContent__date" />
              {isUnread && !isArchivedView && (
                <span className="HeaderNotificationContent__unread-dot" data-testid={unreadDotDataTestId} />
              )}
            </SDSFlexbox>

            <SDSFlexbox className="HeaderNotificationContent__actions" alignItems="center" gap="button-small">
              {isArchivedView ? (
                onUnarchive && (
                  <button
                    type="button"
                    className="HeaderNotificationContent__action-btn"
                    data-testid={unarchiveDataTestId}
                    onClick={e => {
                      e.stopPropagation();
                      onUnarchive();
                    }}
                    aria-label="Unarchive"
                  >
                    <ArrowCounterclockwise size={16} />
                  </button>
                )
              ) : (
                <>
                  {!isUnread && onMarkAsUnread && (
                    <button
                      type="button"
                      className="HeaderNotificationContent__action-btn"
                      data-testid={markUnreadDataTestId}
                      onClick={e => {
                        e.stopPropagation();
                        onMarkAsUnread();
                      }}
                      aria-label="Mark as unread"
                    >
                      <DotSquare size={16} />
                    </button>
                  )}
                  {isUnread && onMarkAsRead && (
                    <button
                      type="button"
                      className="HeaderNotificationContent__action-btn"
                      data-testid={markReadDataTestId}
                      onClick={e => {
                        e.stopPropagation();
                        onMarkAsRead();
                      }}
                      aria-label="Mark as read"
                    >
                      <Check2Square size={16} />
                    </button>
                  )}
                  {onArchive && (
                    <button
                      type="button"
                      className="HeaderNotificationContent__action-btn"
                      data-testid={archiveDataTestId}
                      onClick={e => {
                        e.stopPropagation();
                        onArchive();
                      }}
                      aria-label="Archive"
                    >
                      <Archive size={16} />
                    </button>
                  )}
                </>
              )}
            </SDSFlexbox>
          </SDSFlexbox>

          <SDSText type="body-medium" color="muted" text={message} className="HeaderNotificationContent__message" />
        </SDSFlexbox>
      </SDSFlexbox>
    </div>
  );
}
